import searchStorage from '@Services/SearchStorage';
import { SearchTypes } from '@Constants/CommonConstants';
import Breadcrumb from '@Services/Breadcrumb';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import { retrievePatentFamilyMembers } from '@Containers/PatentFamilyClustering/RetrievePatentFamilyMembersService';
import { searchPatentFamily } from '@Containers/PatentFamilyClustering/SearchPatentFamilyService';
import { getCleanFamilyId, getFirstAndLastSubItem, generateRelativeIndex } from '@Containers/PatentFamilyClustering/utils';
import { enrichParamsWithSemanticFlags } from '@Pages/Results/semanticSearchUtils';
import {
  RESET_PATENT_FAMILY_CLUSTERING_STATE,
  INIT_PATENT_FAMILY_GROUPS,
  EXPAND_PATENT_FAMILY_GROUP,
  SET_PAGE_NUMBER,
  LOAD_PATENT_FAMILY_MEMBERS_START,
  LOAD_PATENT_FAMILY_MEMBERS_SUCCESS,
} from './constants';
import {
  pageNumber as pageNumberSelector,
  groupCount as groupCountSelector,
  loadedPatentFamilyMembersCount as loadedPatentFamilyMembersCountSelector,
} from './selectors';

export const processPatentFamilyGroups = (documents) => {
  return documents.map((document) => {
    const { index, patentFamilyGroup } = document;
    let absoluteIndex;

    if (patentFamilyGroup) {
      const { position, count } = patentFamilyGroup;

      if (Number(count) === 1) {
        // Case for main result item, when there is only one patent in family
        absoluteIndex = Number(position);
      } else if (Number(count) > 1) {
        // Case for main result item, when multiple members in family
        absoluteIndex = [Number(position), Number(position) + Number(count) - 1];
      }
    } else {
      absoluteIndex = Number(index); // Fallback to old indexes
    }

    return {
      ...document,
      absoluteIndex,
    };
  });
};

export const processPatentFamilyMembers = (familyMembers, document) => {
  const { index: mainResultIndex, patentFamilyGroup, databaseId } = document;

  return familyMembers.map((familyMember) => {
    const { index: familyMemberIndex } = familyMember;
    let absoluteIndex; // Number
    let relativeIndex; // String

    if (patentFamilyGroup) {
      const { position } = patentFamilyGroup;
      // Case for loaded family members (expand patent family)
      absoluteIndex = Number(position) + Number(familyMemberIndex) - 1;
      relativeIndex = generateRelativeIndex(mainResultIndex, familyMemberIndex);
    } else {
      // Fallback, should not happen
      Logger.warn('Patent Family Clustering - patentFamilyGroup is not present:', document);
    }

    return {
      ...familyMember,
      databaseId,
      absoluteIndex,
      relativeIndex,
    };
  });
};

export const initPatentFamilyGroups = (documents) => {
  return {
    type: INIT_PATENT_FAMILY_GROUPS,
    payload: documents,
  };
};

export const loadPatentFamilyMembers = ({ document, metadata, firstSubItem, lastSubItem, expand }) => {
  return async (dispatch) => {
    const { index: documentIndex } = document;

    try {
      dispatch({
        type: LOAD_PATENT_FAMILY_MEMBERS_START,
        payload: { document, expand },
      });

      const { resultName, databaseId } = metadata;
      const activeBreadcrumb = Breadcrumb.getBreadcrumbForDbByResultName(databaseId, resultName);

      const paramsWithSemantic = enrichParamsWithSemanticFlags(
        {
          ...metadata,
          documentIndex,
          firstSubItem: expand ? firstSubItem + 1 : firstSubItem, // In case of expand request from 2nd item
          lastSubItem,
        },
        activeBreadcrumb
      );

      const patentFamilyMembers = await retrievePatentFamilyMembers(paramsWithSemantic);
      const processedPatentFamilyMembers = processPatentFamilyMembers(patentFamilyMembers, document);

      dispatch({
        type: LOAD_PATENT_FAMILY_MEMBERS_SUCCESS,
        payload: { document, expand, patentFamilyMembers: processedPatentFamilyMembers },
      });
    } catch (error) {
      Logger.error(error);
    }
  };
};

export const expandPatentFamilyGroup = ({ document, metadata }) => {
  return (dispatch, getState) => {
    const pageNumber = pageNumberSelector(getState(), { document });
    const groupCount = groupCountSelector(getState(), { document });
    const loadedPatentFamilyMembersCount = loadedPatentFamilyMembersCountSelector(getState(), { document });
    const { firstSubItem, lastSubItem } = getFirstAndLastSubItem({ pageNumber, groupCount });

    dispatch({
      type: EXPAND_PATENT_FAMILY_GROUP,
      payload: { document, isExpanded: true },
    });

    if (loadedPatentFamilyMembersCount < lastSubItem - firstSubItem + 1) {
      dispatch(loadPatentFamilyMembers({ document, metadata, firstSubItem, lastSubItem, expand: true }));
    }
  };
};

export const collapsePatentFamilyGroup = ({ document }) => {
  return {
    type: EXPAND_PATENT_FAMILY_GROUP,
    payload: { document, isExpanded: false },
  };
};

export const setPageNumber = ({ document, metadata, pageNumber }) => {
  return (dispatch, getState) => {
    const groupCount = groupCountSelector(getState(), { document });
    const { firstSubItem, lastSubItem } = getFirstAndLastSubItem({ pageNumber, groupCount });

    dispatch({
      type: SET_PAGE_NUMBER,
      payload: { document, pageNumber },
    });

    dispatch(loadPatentFamilyMembers({ document, metadata, firstSubItem, lastSubItem, expand: false }));
  };
};

export const viewFullPatentFamily = ({ document, metadata }) => {
  return async () => {
    const { promise } = searchPatentFamily({ document, metadata });
    const response = await promise;

    const { resultSize } = response;
    const searchId = await searchStorage.setNew({
      searchType: SearchTypes.DOCUMENT_SEARCH,
    });

    const searchParams = {
      ...response,
      searchId,
      resetBreadcrumbs: false,
      expandPatentFamilyGroup: getCleanFamilyId(document),
    };

    await Breadcrumb.createBreadcrumb(searchParams, resultSize);
    ResultsDataManage.redirectToResults(searchParams);
  };
};

export const resetPatentFamilyClusteringState = () => {
  return {
    type: RESET_PATENT_FAMILY_CLUSTERING_STATE,
  };
};
